import { Injectable } from '@angular/core';
import { CaeItem, CaeLevel, CaeRail, createCaeItem } from '@project/cae/cae-data.service';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CaeProjectHelper } from '@project/processor/cae/cae-project-helper';
import { ProjectModel } from '@project/model/project.model';
import { AcceptingConnectionSortGroupHelper } from '@3d-scene/helper/connection/accepting-connection-sort-group.helper';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { Dictionary } from 'typescript-collections';
import { ConnectionSearchResultVO } from '@project/components/article/model/vo/connection-search-result.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { AccessoryService, ConnectionSearchResult } from '@article/service/accessory.service';
import { lastValueFrom } from 'rxjs';
import { SuitableConnectionsVO } from '@project/components/article/model/vo/suitable-connections.vo';
import { CaeImportHelperService } from './cae-import-helper.service';
import { PropertyCreator } from '@article/util/property-creator';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleCreationManager } from '@project/components/article/manager/article-creation.manager';
import { ArticleCreationManagerFactory } from '@project/components/article/manager/article-creation.manager.factory';
import { CreateLabelingHelper } from '@project/components/label/helper/create-labeling.helper';
import { ConvertLabelingArticleCommand } from '@project/processor/cae/convert-labeling-article.command';
import { MetaElementFeaturePropertyDef } from '@project/constants/meta-element-feature-property-def';
import { LabelingArticleConversionDataManager } from '@project/components/article/manager/labeling-article-conversion-data.manager';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CompressionSimpleTextValueVO, ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO, TableTextItemVO, TableTextRowVO, TableTextValueVO, TextureAreaUsageVO, TextureAreaVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { SuitableConnectionAreasVO } from '@project/components/article/model/vo/suitable-connection-areas.vo';
import { Log } from '@shared/debug/log';
import { ConnectionLevel } from '@project/cae/util/connection-level';
import { ConnectionLevelUtils } from '@project/cae/util/connection-level-utils';
import { StringUtils } from '@shared/util/string-utils';
import { TextCheckSpacingData } from '@3d-scene/manager/text-check-spacing-data';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { TextSizeCheckRequestVO, TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { TableProviderUtils } from '@project/components/label/label-editor/service/table-provider-utils';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { TextSizeCheckUtils } from '@shared/util/text-size-check-utils';
import { InitManager } from '@shared/manager/init.manager';
import { DefaultTextParameters } from '@project/components/label/label-editor/default-text-parameters';
import { BmkParser, BmkParseResult } from './bmk-parser';
import { CaeImportOptions } from './cae-import-options.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { DefaultTextParametersManager } from '@project/components/article/manager/default-text-parameters.manager';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { CaeImportErrorCollector } from '../cae-import-error-collector';
import { CaeImportException } from '@project/cae/cae-import-exception';
import { LabelingCategory } from '@article/labeling/labeling.category';
import { CaeRailsData } from './cae-rails-data';
import { HtmlUtils } from '@shared/util/html-utils';

type ShouldSplitLabelsMap = Map<string, { split: boolean, currentIteration: number }>;

type BmkOptions = Pick<CaeImportOptions,
  | 'addLabelBmk'
  | 'addEndstopBmk'
  | 'bmkFunctionalAssignment'
  | 'bmkFunctionDesignation'
  | 'bmkLocationDesignation'
  | 'bmkInstallationSite'
  | 'bmkDevice'
  | 'textRotation'
  | 'levelTopDown'
  | 'labelingCategory'
>;

class FavoriteArticleProvider {

  private _favoriteLabelMap: Dictionary<number, ConnectionSearchResultVO> = new Dictionary<number, ConnectionSearchResultVO>();

  constructor(
    private caeImportHelperService: CaeImportHelperService,
    private propertyCreator: PropertyCreator,
    private accessoryService: AccessoryService,
    private articleManager: ArticleManager,
    private caeImportErrorCollector: CaeImportErrorCollector,
  ) {
  }

  public async getFavoriteArticle(caeItem: CaeItem, labelingCategory: LabelingCategory): Promise<ArticleVO | undefined> {
    const onRailArticleContainers: ArticleContainer[] = this.caeImportHelperService.getArticleContainersForProjectArticle(caeItem);
    if (onRailArticleContainers.length === 0) {
      return undefined;
    }

    let adapter = onRailArticleContainers.find((container) => ArticleUtils.isAdapterArticle(container.article));
    const baseContainers = adapter ? [ adapter ] : ArticleContainerUtils.getBaseArticleContainers(onRailArticleContainers);

    const acceptingCubes: Connection[] = ConnectionInfoUtils.getAcceptingConnections(baseContainers);
    const requirements: ConnectionCubeSearchRequirements[] = this.propertyCreator.createCubesSearchRequirementsByDefault(acceptingCubes);

    const connectionSearchResult = this._favoriteLabelMap.getValue(onRailArticleContainers[0].articleId);
    if (connectionSearchResult) {
      return this.favoriteArticleByRequirementsLoaded(connectionSearchResult, caeItem);
    } else {
      const connectionSearchResult: ConnectionSearchResult =
        await lastValueFrom(this.accessoryService.getLabelingFavoriteArticlesByRequirementsAndLabelingCategories(requirements, [ labelingCategory ]));
      return this.favoriteArticleByRequirementsLoaded(connectionSearchResult.vo, caeItem);
    }
  }

  private async favoriteArticleByRequirementsLoaded(connectionSearchResult: ConnectionSearchResultVO, caeItem: CaeItem): Promise<ArticleVO | undefined> {
    const onRailArticleContainers: ArticleContainer[] = this.caeImportHelperService.getArticleContainersForProjectArticle(caeItem);
    this._favoriteLabelMap.setValue(onRailArticleContainers[0].articleId, connectionSearchResult);

    if (connectionSearchResult.connections && connectionSearchResult.connections.length > 0) {
      const connection: SuitableConnectionsVO = connectionSearchResult.connections[0];
      return await lastValueFrom(this.articleManager.loadFullArticle(connection.article.identity, connection.article.version));
    } else {
      const error = this.caeImportErrorCollector.createImportErrorForItem(caeItem, 'caeImport.marking.error.no_favorite_label_found');
      throw new CaeImportException(error);
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class CaeImportLabelsService {

  private caeImportErrorCollector!: CaeImportErrorCollector;
  private articleCreationManager!: ArticleCreationManager;

  constructor(
    private caeProjectHelper: CaeProjectHelper,
    private projectModel: ProjectModel,
    private acceptingConnectionSortGroupHelper: AcceptingConnectionSortGroupHelper,
    private caeImportHelperService: CaeImportHelperService,
    private propertyCreator: PropertyCreator,
    private accessoryService: AccessoryService,
    private articleManager: ArticleManager,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private createLabelingHelper: CreateLabelingHelper,
    private labelingArticleConversionDataManager: LabelingArticleConversionDataManager,
    private containerService: ContainerService,
    private projectArticleService: ProjectArticleService,
    private projectMetaElementService: ProjectMetaElementService,
    private labelingUtils: LabelingUtils,
    private textSizeCheckManager: TextSizeCheckManager,
    private applicationRefreshService: ApplicationRefreshService,
    private connectionInfoService: ConnectionInfoService,
    private defaultTextParametersManager: DefaultTextParametersManager,
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
  ) {
    this.articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    this.articleCreationManager.showFaultMessage = false;
  }

  public async addLabels(caeRailsData: CaeRailsData, bmkOptions: BmkOptions, caeImportErrorCollector: CaeImportErrorCollector): Promise<void> {

    this.caeImportErrorCollector = caeImportErrorCollector;

    const favoriteArticleProvider = new FavoriteArticleProvider(
      this.caeImportHelperService,
      this.propertyCreator,
      this.accessoryService,
      this.articleManager,
      caeImportErrorCollector,
    );

    for (const caeRail of caeRailsData.currentRails) {

      this.caeImportErrorCollector.processRail(caeRail);

      // Workaround is needed because otherwise there are too many requests that try to create labels.
      this.labelingArticleConversionDataManager.stopPostProcess = true;
      this.projectModel.texturesEnabled = false;
      this.acceptingConnectionSortGroupHelper.allowAdaptersOverspanningUnconnectedRailArticles = false;
      try {
        await this.processRail(caeRail, favoriteArticleProvider, bmkOptions);
      } finally {
        this.acceptingConnectionSortGroupHelper.allowAdaptersOverspanningUnconnectedRailArticles = true;
        this.projectModel.texturesEnabled = true;
        this.labelingArticleConversionDataManager.stopPostProcess = false;
      }
    }

    await this.applicationRefreshService.refreshProject();
  }

  private getCarriers(caeItem: CaeItem, article: ArticleVO | undefined, labelArticle: ArticleVO) {
    const carrierItems = [];
    if (article && ArticleUtils.isEndStopArticle(article)) {
      const carriers = this.findLabelCarrier(caeItem);
      for (const carrier of carriers) {
        carrierItems.push(this.convertToItemCaeXml(carrier, labelArticle));
      }
    }
    return carrierItems;
  }

  private findLabelArticle(caeItem: CaeItem): ArticleVO | undefined {
    for (const accessory of caeItem.items) {
      const article = this.caeProjectHelper.getArticleByCaeBaseArticle(accessory);
      if (article && ArticleUtils.isLabelingArticle(article)) {
        return article;
      }
    }
  }

  private findLabelCarrier(caeItem: CaeItem): CaeItem[] {
    const carrier: CaeItem[] = [];
    for (const accessory of caeItem.items) {
      const article = this.caeProjectHelper.getArticleByCaeBaseArticle(accessory);
      if (article && ArticleUtils.isArticleOfTypeId(article, ArticleTypeIds.MARKER_CARRIER)) {
        carrier.push(accessory);
      }
    }
    return carrier;
  }

  private convertToItemCaeXml(carrierItem: CaeItem, labelArticle: ArticleVO): CaeItem {
    return {
      ...carrierItem,
      items: [ createCaeItem({articleNo: labelArticle.articleNo}) ],
    };
  }

  private async processRail(caeRail: CaeRail, favoriteArticleProvider: FavoriteArticleProvider, bmkOptions: BmkOptions) {

    const createdArticleContainers: ArticleContainer[] = [];
    const shouldSplitLabelsMap: ShouldSplitLabelsMap = new Map<string, { split: boolean, currentIteration: number }>();

    for (const caeItem of caeRail.items) {
      await this.processItem(caeItem, favoriteArticleProvider, bmkOptions, shouldSplitLabelsMap, createdArticleContainers);
    }

    await this.updateDynamicArticles(createdArticleContainers);
    this.createLabelingHelper.correctTextureSequenceForAdapters();
    await this.updateTextures(createdArticleContainers);
    await this.createLabelText(caeRail, shouldSplitLabelsMap, bmkOptions);
  }

  private async processItem(caeItem: CaeItem, favoriteArticleProvider: FavoriteArticleProvider, bmkOptions: BmkOptions, shouldSplitLabelsMap: ShouldSplitLabelsMap, createdArticleContainers: ArticleContainer[]) {
    const article = this.caeProjectHelper.getArticleByCaeBaseArticle(caeItem);
    const labelArticle = this.findLabelArticle(caeItem);
    if (labelArticle) {
      await this.processKnownLabelItem(caeItem, labelArticle, shouldSplitLabelsMap, createdArticleContainers);
      if (bmkOptions.addEndstopBmk) {
        const carriers = this.getCarriers(caeItem, article, labelArticle);
        for (const carrier of carriers) {
          await this.processKnownLabelItem(carrier, labelArticle, shouldSplitLabelsMap, createdArticleContainers);
        }
      }
    } else if (caeItem.levels && caeItem.levels.length > 0 && !(article && ArticleUtils.isEndStopArticle(article))) {
      await this.processUnknownLabelItem(caeItem, favoriteArticleProvider, bmkOptions.labelingCategory, shouldSplitLabelsMap, createdArticleContainers);
    } else {
      if (bmkOptions.addEndstopBmk) {
        const carriers = this.getCarriers(caeItem, article, new ArticleVO());
        for (const carrier of carriers) {
          await this.processUnknownLabelItem(carrier, favoriteArticleProvider, 'strip', shouldSplitLabelsMap, createdArticleContainers);
        }
      }
    }
  }

  private async processLabelItem(caeItem: CaeItem, labelArticle: ArticleVO, shouldSplitLabelsMap: ShouldSplitLabelsMap) {
    const shouldSplitLabels = await this.connectLabelAccessory(caeItem, labelArticle);
    shouldSplitLabelsMap.set(caeItem.id, {split: shouldSplitLabels, currentIteration: 0});
  }

  private async processKnownLabelItem(caeItem: CaeItem, labelArticle: ArticleVO, shouldSplitLabelsMap: ShouldSplitLabelsMap, createdArticleContainers: ArticleContainer[]) {
    this.articleCreationManager.clean();
    try {
      await this.processLabelItem(caeItem, labelArticle, shouldSplitLabelsMap);
    } catch (error) {
      if (error instanceof CaeImportException) {
        this.caeImportErrorCollector.addError(error.caeError);
      } else {
        this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.marking.error.connect');
      }
    }
    createdArticleContainers.push(...this.articleCreationManager.getCreatedArticleContainers());
  }

  private async processUnknownLabelItem(caeItem: CaeItem, favoriteArticleProvider: FavoriteArticleProvider, labelCategory: LabelingCategory, shouldSplitLabelsMap: ShouldSplitLabelsMap, createdArticleContainers: ArticleContainer[]) {
    this.articleCreationManager.clean();
    try {
      const labelArticle = await favoriteArticleProvider.getFavoriteArticle(caeItem, labelCategory);
      await this.processLabelItem(caeItem, labelArticle, shouldSplitLabelsMap);
    } catch (error) {
      if (error instanceof CaeImportException) {
        this.caeImportErrorCollector.addError(error.caeError);
      } else {
        this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.marking.error.connect');
      }
    }
    createdArticleContainers.push(...this.articleCreationManager.getCreatedArticleContainers());
  }

  private async connectLabelAccessory(caeItem: CaeItem, article: ArticleVO): Promise<boolean> {

    const onAdapter = this.findLabelingAdapter(caeItem);

    const freeAcceptingConnections: Connection[] = onAdapter ?
      ConnectionInfoUtils.getAcceptingConnections([ onAdapter ])
      : this.getFilteredAcceptingCubes(caeItem, article);

    const givingAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(article, ConnectionAreaVO.SIDE_GIVING);
    const suitableConnection: SuitableConnectionAreasVO = SuitableConnectionAreasVO.create(article, givingAreas);

    // split the labels rows if it will be printed on an adapter, so each level can be printed on a separate strip on the adapter
    const separateRowsOntoMultipleLabelingStrips = !!onAdapter && freeAcceptingConnections.length > 1;

    try {
      // create the labelingStrips at the calculated connections
      await this.articleCreationManager.createArticlesBySuitableGivingConnectionAreasAndAcceptingCubes(suitableConnection, freeAcceptingConnections);
    } catch (error) {
      Log.error('Could not connect label accessory: ', error);
    }

    return separateRowsOntoMultipleLabelingStrips;
  }

  private getFilteredAcceptingCubes(caeItem: CaeItem, labelArticle: ArticleVO): Connection[] {

    const onRailArticleContainers = this.caeImportHelperService.getArticleContainersForProjectArticle(caeItem);

    if (onRailArticleContainers.length === 0) {
      const error = this.caeImportErrorCollector.createImportErrorForItem(caeItem, 'caeImport.marking.error.articleNotFound');
      throw new CaeImportException(error);
    }

    if (ArticleUtils.isDynamicLabelingArticle(labelArticle)) {
      return ConnectionInfoUtils.getAcceptingConnections(ArticleContainerUtils.getBaseArticleContainers(onRailArticleContainers));
    } else {
      return this.getFilteredAcceptingCubesByMarkingArticle(onRailArticleContainers, caeItem);
    }
  }

  private getFilteredAcceptingCubesByMarkingArticle(onRailArticleContainers: ArticleContainer[], item: CaeItem): Connection[] {
    let filteredAcceptingCubes: Connection[] = [];
    const baseContainers: ArticleContainer[] = ArticleContainerUtils.getBaseArticleContainers(onRailArticleContainers);
    const acceptingCubesByContainer: Connection[] = this.getFilteredAcceptingCubesByLevels(item, baseContainers[0]);
    filteredAcceptingCubes = filteredAcceptingCubes.concat(acceptingCubesByContainer);
    return filteredAcceptingCubes;
  }

  private getFilteredAcceptingCubesByLevels(caeItem: CaeItem, articleContainer: ArticleContainer): Connection[] {
    if (!caeItem.levels || caeItem.levels.length === 0) {
      return ConnectionInfoUtils.getAcceptingConnections([ articleContainer ]);
    }
    // consider levelNr-properties only for base parts of complex article, otherwise use geometric position to determine levels
    const connectionLevels: ConnectionLevel[] = ConnectionLevelUtils.createConnectionLevelsByProperties(articleContainer);
    let filteredAcceptingCubes: Connection[] = [];
    for (const level of caeItem.levels) {
      const number: number = level.number;

      const connectionLevel: ConnectionLevel = ConnectionLevelUtils.findConnectionLevel(number, connectionLevels);
      if (connectionLevel) {
        filteredAcceptingCubes = filteredAcceptingCubes.concat(connectionLevel.getFilteredAcceptingCubes());
      } else if (StringUtils.isEmpty(level.name)) {
        continue;
      } else {
        this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.marking.error.missing_level', [ StringUtils.defaultValue(number, 'NaN') ]);
      }
    }
    return filteredAcceptingCubes;
  }

  private findLabelingAdapter(item: CaeItem): ArticleContainer | undefined {
    const containers = this.caeImportHelperService.getArticleContainersForProjectArticle(item);
    for (const container of containers) {
      // in case the rail-article already has an adapter by default, it's already present in the containers here so we can just return it directly
      if (ArticleUtils.isAdapterArticle(container.article)) {
        return container;
      }

      // ... if the article usually doesn't come with an adapter but has one defined in its accessorys,
      // we added it in a previous step and now need to find it in the connected containers.
      const connectedContainers = this.containerService.getConnectedArticleContainers(container.articleUsage);
      for (const connectedContainer of connectedContainers) {
        if (ArticleUtils.isAdapterArticle(connectedContainer.article)) {
          return connectedContainer;
        }
      }
    }
  }

  private async updateDynamicArticles(createdArticleContainers: ArticleContainer[]): Promise<void> {
    const labelArticleIds = createdArticleContainers.filter(c => ArticleUtils.isDynamicLabelingArticle(c.article)).map(a => a.articleUsage.id);
    if (labelArticleIds && labelArticleIds.length > 1) {
      await new ConvertLabelingArticleCommand(
        this.propertyCreator,
        this.labelingArticleConversionDataManager,
        this.containerService,
        this.articleCreationManagerFactory,
        this.accessoryService,
      ).execute({
        selectedUsageIds: labelArticleIds,
        convertToType: MetaElementFeaturePropertyDef.TYPE_MARKING_STRIP_PREFIX,
      });
    }
  }

  private async updateTextures(createdArticleContainers: ArticleContainer[]): Promise<void> {

    const textSizeChecksToRetrieving: TextSizeCheckRequestVO[] = [];
    for (const labelArticleContainer of createdArticleContainers) {
      this.createTextureUsages(labelArticleContainer);

      const textureElements: TextureAreaVO[] = ArticleUtils.getTextureElements(labelArticleContainer.article);
      for (const textureArea of textureElements) {
        if (!textureArea) {
          continue;
        }

        const textureAreaName: string = textureArea.name;
        const textureConnection: Connection = labelArticleContainer.findConnectionByName(textureAreaName);
        const textCheckSpacingData: TextCheckSpacingData = this.labelingUtils.getTextCheckSpacingDataByTextureCube(textureConnection);
        const textSizeCheckClass: string = PropertyUtils.getStringPropertyValue(textureArea.behaviorProperties,
          MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS);
        const labelSizeInfos: TextSizeCheckVO[] = this.textSizeCheckManager.getTextSizeCheckWithFallback(
          textSizeCheckClass,
          textCheckSpacingData,
          ArticleUtils.isDynamicLabelingArticle(textureConnection.article),
        );
        if (ArrayUtils.isEmpty(labelSizeInfos)) {
          const req: TextSizeCheckRequestVO = this.textSizeCheckManager.getTextSizeCheckRequest(
            textSizeCheckClass,
            textCheckSpacingData,
          );
          if (req) {
            textSizeChecksToRetrieving.push(req);
          }
        }
      }
    }

    await this.textSizeCheckManager.retrieveTextSizeCheckInfos(textSizeChecksToRetrieving);
  }

  private createTextureUsages(articleContainer: ArticleContainer): void {
    if (!articleContainer || ArticleUtils.isDynamicArticle(articleContainer.article)) {
      return;
    }
    const textureMetaElements: TextureAreaVO[] = ArticleUtils.getTextureElements(articleContainer.article);
    for (const textureArea of textureMetaElements) {
      const cube: Connection = articleContainer.findFirstConnectionByName(textureArea.name);
      if (cube) {
        LabelingUtils.applyTextureMetaElementUsageIfNotExists(cube, this.projectArticleService, this.projectMetaElementService);
      }
    }
  }

  private async createLabelText(caeRail: CaeRail, shouldSplitLabelsMap: ShouldSplitLabelsMap, bmkOptions: BmkOptions): Promise<void> {

    const createdLabelingStrips = this.getCreatedLabelingStrips(caeRail);

    for (const {articleContainer, baseCaeItems} of createdLabelingStrips) {
      const caeItems = this.getWithLabelingItemsInBetween(baseCaeItems, caeRail, articleContainer);
      const article = this.caeProjectHelper.getArticleByCaeBaseArticle(caeItems[0]);
      const isEndStop = !!article && ArticleUtils.isEndStopArticle(article);
      const levelsEmpty = caeItems.length === 0 || caeItems.every(item => item.levels.length === 0);

      if (!isEndStop && levelsEmpty) {
        continue;
      }

      const textureElements: TextureAreaVO[] = ArticleUtils.getTextureElements(articleContainer.article);
      const textureArea: TextureAreaVO | undefined = textureElements.find(t => !!t);

      if (textureArea) {
        this.caeImportHelperService.equalizeItemLevels(caeItems);

        const labels: TextValueVO[] = this.createLabelTextValues(articleContainer, textureArea, caeItems, shouldSplitLabelsMap, isEndStop, bmkOptions);
        this.updateLabelOnTextureUsage(labels, articleContainer.articleUsage);
      }
    }
    await this.refreshTextureConnections(createdLabelingStrips.map((strip) => strip.articleContainer));
  }

  /** @returns all previously created labelingStrip Containers and the corresponding caeItems, sorted by their elevation */
  private getCreatedLabelingStrips(caeRail: CaeRail): { articleContainer: ArticleContainer, baseCaeItems: CaeItem[] }[] {
    return ProjectUtils.articleUsages(this.projectModel.currentProject)
      .map(usage => this.containerService.findArticleContainerInGlobal(usage.id))
      .map(articleContainer => ({articleContainer, baseCaeItems: this.getBaseCaeItems(articleContainer, caeRail)}))
      .filter(({articleContainer, baseCaeItems}) =>
        ArticleUtils.isLabelingArticle(articleContainer.article) && baseCaeItems.length !== 0)
      .sort((a, b) => a.articleContainer.position.y - b.articleContainer.position.y);
  }

  /** @returns The corresponding caeItem(s), to an articleContainer */
  private getBaseCaeItems(labelingContainer: ArticleContainer, caeRail: CaeRail): CaeItem[] {
    const items: CaeItem[] = [];
    const usageIds: string[] = this.containerService.getBackwardConnectedArticleUsageIdsWithComposite(labelingContainer.articleUsage);
    for (const id of usageIds) {
      for (const item of caeRail.items) {
        if (item.id === id || (item.items && item.items.filter(a => a.id === id).length > 0)) {
          items.push(item);
          break;
        }
      }
    }
    return items;
  }

  private getWithLabelingItemsInBetween(baseCaeItems: CaeItem[], caeRail: CaeRail, givingArticleContainer: ArticleContainer) {
    const indexOfFirst = caeRail.items.indexOf(baseCaeItems.at(0));
    const indexOfLast = caeRail.items.indexOf(baseCaeItems.at(-1));
    const itemsWithItemsInBetween = caeRail.items.slice(indexOfFirst, indexOfLast + 1);
    const guids: string[] = givingArticleContainer.properties.getValue(ArticleContainerPropertyDef.ARTICLES_GUIDS_BETWEEN_ADAPTERS);
    const articleContainers = this.containerService.findArticleContainersInGlobalByGuids(guids);
    const compositeOrArticleIds = articleContainers.map((articleContainer) => ArticleUsageUtils.getCompositeOrArticleUsageId(articleContainer.articleUsage));
    return itemsWithItemsInBetween.filter((item) => baseCaeItems.includes(item) || compositeOrArticleIds.includes(item.id));
  }

  private createLabelTextValues(markingStripContainer: ArticleContainer,
                                textureElement: TextureAreaVO,
                                labeledCaeArticles: CaeItem[],
                                shouldSplitLabelsMap: ShouldSplitLabelsMap,
                                isEndStop: boolean,
                                bmkOptions: BmkOptions): TextValueVO[] {
    if (!markingStripContainer || !labeledCaeArticles || labeledCaeArticles.length === 0) {
      return null;
    }

    const splitLabels = shouldSplitLabelsMap.get(labeledCaeArticles[0]?.id)?.split;
    const isFixedSizeArticle = !ArticleUtils.isDynamicLabelingArticle(markingStripContainer.article);

    const levelTopDown = bmkOptions.levelTopDown;
    const textRotation = bmkOptions.textRotation;

    if (isFixedSizeArticle && splitLabels) {
      return this.createSingleTableTextValues(markingStripContainer, textureElement, labeledCaeArticles, shouldSplitLabelsMap, levelTopDown, textRotation);
    }
    if (isFixedSizeArticle) return this.createSimpleTextValue(markingStripContainer, textureElement, labeledCaeArticles, bmkOptions);
    if (isEndStop) return this.createBmkTextValue(markingStripContainer, textureElement, labeledCaeArticles, bmkOptions);
    if (!bmkOptions.addLabelBmk) return this.createSingleTableTextValues(markingStripContainer, textureElement, labeledCaeArticles, shouldSplitLabelsMap, levelTopDown, textRotation);
    return this.createTableTextValueWithBmk(markingStripContainer, textureElement, labeledCaeArticles, shouldSplitLabelsMap, bmkOptions);
  }

  private createSimpleTextValue(articleContainer: ArticleContainer,
                                textureElement: TextureAreaVO,
                                markingArticle: CaeItem[],
                                bmkOptions: BmkOptions): TextValueVO[] {
    let cellValue = '';
    if (ArticleUtils.isEndStopArticle(this.caeProjectHelper.getArticleByCaeBaseArticle(markingArticle[0]))) {
      cellValue = this.getLabelOrBmk(markingArticle[0].levels[0], markingArticle[0].structure, bmkOptions);

    } else {
      cellValue = this.getMarkingTextByLevel(articleContainer, markingArticle[0]);
    }
    const font = this.calcFontSize(articleContainer, textureElement, cellValue, 1, 1, bmkOptions.textRotation);
    return [ this.createCompressionSimpleText(cellValue, font.fontSize, font.compression, bmkOptions.textRotation) ];
  }

  private getMarkingTextByLevel(articleContainer: ArticleContainer,
                                markingArticle: CaeItem): string {
    let textLabel: string = '';

    const parentIdentity: MetaElementUsageIdentityVO = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(articleContainer.articleUsage);
    const acceptingCube: Connection = this.connectionInfoService.findConnectionByIdentity(parentIdentity);
    let level = ConnectionLevelUtils.getConnectionLevelByProperty(acceptingCube.metaElementVO);
    Log.assert(!isNaN(level), 'level could not be determined');
    if (NumberUtils.isNotNumber(level)) {
      level = 1;
    }
    const line = markingArticle.levels.find(l => l.number === level);
    if (line) {
      textLabel = line.name;
    }
    return textLabel;
  }

  private createBmkTextValue(articleContainer: ArticleContainer,
                             textureElement: TextureAreaVO,
                             labeledCaeItems: CaeItem[],
                             bmkOptions: BmkOptions): TextValueVO[] {
    const tableText: TableTextValueVO = new TableTextValueVO();
    const textRow: TableTextRowVO = this.createBmkTableRow(articleContainer, textureElement, 1, labeledCaeItems, bmkOptions);
    tableText.rows.push(textRow);
    return [ tableText ];
  }

  /*
    Wenn kein BMK angezeigt wird, können einzelne TextValue-Objekte pro Klemme erstellt werden.
     Das hat den Vorteil, dass die generierten Texturen kleiner sind.
     Es kann sonst passieren, dass eine Texture so groß wird, dass diese nicht mehr vom Browser angezeigt wird.
  */
  private createSingleTableTextValues(articleContainer: ArticleContainer,
                                      textureElement: TextureAreaVO,
                                      labeledCaeitems: CaeItem[],
                                      shouldSplitLabelsMap: ShouldSplitLabelsMap,
                                      levelTopDown: boolean,
                                      textRotation: number): TextValueVO[] {
    for (const labeledCaeArticle of labeledCaeitems) {
      if (shouldSplitLabelsMap.get(labeledCaeArticle.id)?.split) {
        shouldSplitLabelsMap.get(labeledCaeArticle.id).currentIteration++;
      }
    }

    const textValues: TableTextValueVO[] = [];

    for (const item of labeledCaeitems) {
      const isSplit = shouldSplitLabelsMap.get(item.id)?.split;
      const levels = item.levels.length;
      const rows = isSplit ? 1 : levels;
      const tableText = new TableTextValueVO();

      for (let i = 1; i <= rows; i++) {
        let row = isSplit ? shouldSplitLabelsMap.get(item.id).currentIteration : i;
        if (!levelTopDown) {
          row = levels - row + 1;
        }
        const textRow = new TableTextRowVO();

        const levelInfo: CaeLevel = item.levels[row - 1];
        const rowItem: TableTextItemVO = this.createRowItem(item, articleContainer, textureElement, rows, levelInfo, textRotation);
        if (rowItem.colSpan === 0) {
          // For each element there should always be at least one column.
          rowItem.colSpan = 1;
        }
        textRow.items.push(rowItem);

        tableText.rows.push(textRow);
      }
      textValues.push(tableText);
    }

    const isMultiRowTable = textValues.some(textValue => textValue.rows.length > 1);

    if (isMultiRowTable) {
      return textValues;
    }

    const row = new TableTextRowVO();
    row.items = textValues.flatMap(textValue => textValue.rows[0]?.items ?? []);

    const textValue = new TableTextValueVO();
    textValue.rows = [ row ];
    return [ textValue ];
  }

  private createTableTextValueWithBmk(articleContainer: ArticleContainer,
                                      textureElement: TextureAreaVO,
                                      markingArticle: CaeItem[],
                                      shouldSplitLabelsMap: ShouldSplitLabelsMap,
                                      bmkOptions: BmkOptions): TextValueVO[] {

    const tableText: TableTextValueVO = new TableTextValueVO();
    const levels = Math.max(...markingArticle.map(a => a.levels.length), 0);
    const isSplit = markingArticle.map(ma => shouldSplitLabelsMap.get(ma.id)).every(s => s.split);
    const rows = isSplit ? 1 : levels;
    const rowsWithBmk = rows + 1;

    const textRow: TableTextRowVO = this.createBmkTableRow(articleContainer, textureElement, rowsWithBmk, markingArticle, bmkOptions);
    tableText.rows.push(textRow);

    for (let i = 1; i <= rows; i++) {
      const textRow: TableTextRowVO = new TableTextRowVO();
      for (const item of markingArticle) {
        if (shouldSplitLabelsMap.get(item.id)?.split) {
          shouldSplitLabelsMap.get(item.id).currentIteration++;
        }

        let row = isSplit ? shouldSplitLabelsMap.get(item.id).currentIteration : i;
        if (!bmkOptions.levelTopDown) {
          row = levels - row + 1;
        }

        const levelInfo: CaeLevel = item.levels[row - 1];
        const rowItem: TableTextItemVO = this.createRowItem(item, articleContainer, textureElement, rowsWithBmk, levelInfo, bmkOptions.textRotation);
        textRow.items.push(rowItem);

      }
      tableText.rows.push(textRow);
    }
    return [ tableText ];
  }

  private createRowItem(caeItem: CaeItem,
                        articleContainer: ArticleContainer,
                        textureElement: TextureAreaVO,
                        rows: number,
                        levelInfo: CaeLevel,
                        textRotation: number): TableTextItemVO {
    let text = '';
    if (levelInfo) {
      text = levelInfo.name;
    }

    const rowspan = TableProviderUtils.calculateLineSpan(articleContainer.connections) / rows;
    const columns = this.countLabelConnectionsLinkedToItem(articleContainer, caeItem);

    const font = this.calcFontSize(articleContainer, textureElement, text, rows, columns, textRotation);
    const rowItem: TableTextItemVO = new TableTextItemVO();
    rowItem.colSpan = columns;
    rowItem.rowSpan = rowspan;
    rowItem.value = this.createCompressionSimpleText(text, font.fontSize, font.compression, textRotation);
    return rowItem;
  }

  private countLabelConnectionsLinkedToItem(labelingContainer: ArticleContainer, item: CaeItem): number {
    const caeItemArticleUsageId: string = item.id;

    return ArticleUsageUtils.connectionAreaUsages(labelingContainer.articleUsage)
      .filter(connectionAreaUsage => this.isBackwardConnectionTo(connectionAreaUsage, caeItemArticleUsageId))
      .length;
  }

  private isBackwardConnectionTo(connectionAreaUsage: ConnectionAreaUsageVO, otherArticleUsageId: string): boolean {
    const connectedArticleUsageId = ConnectionAreaUsageUtils.backwardConnectedArticleUsageId(connectionAreaUsage);
    if (!connectedArticleUsageId) {
      // Not a backward connection
      return false;
    }

    // check for a direct connection to otherArticleUsageId
    if (connectedArticleUsageId === otherArticleUsageId) {
      return true;
    }

    // check if the connected article is a composite Article
    const articleContainer = this.containerService.findArticleContainerInGlobal(connectedArticleUsageId);
    const compositeArticleUsageId = CompositeArticleInfoUtils.getCompositeArticleUsageId(articleContainer);
    if (compositeArticleUsageId === otherArticleUsageId) {
      return true;
    }

    // recursion: check if the connected article has backward connections to otherArticleUsageId
    const articleUsage: ArticleUsageVO = ProjectUtils.findArticleUsageInProjectById(this.projectModel.currentProject, connectedArticleUsageId);
    return ArticleUsageUtils.connectionAreaUsages(articleUsage)
      .some(parentConnectionId => this.isBackwardConnectionTo(parentConnectionId, otherArticleUsageId));
  }

  private createBmkTableRow(articleContainer: ArticleContainer,
                            textureElement: TextureAreaVO,
                            rows: number,
                            markingArticle: CaeItem[],
                            bmkOptions: BmkOptions): TableTextRowVO {

    const maxrowspan = TableProviderUtils.calculateLineSpan(articleContainer.connections);
    const rowspan: number = maxrowspan / (rows);

    const bmkGroups = ArrayUtils.groupByPreserveOrder(markingArticle, (item) => item.structure);

    const textRow: TableTextRowVO = new TableTextRowVO();

    for (const bmkGroup of bmkGroups) {
      const colSpan = this.findNumberOfConnectedConnections(articleContainer, bmkGroup.items);
      const text = this.createBmk(bmkGroup.groupId, bmkOptions);
      const font = this.calcFontSize(articleContainer, textureElement, text, rows, colSpan, bmkOptions.textRotation);
      const rowItem: TableTextItemVO = new TableTextItemVO();
      rowItem.colSpan = colSpan;
      rowItem.rowSpan = rowspan;
      rowItem.value = this.createCompressionSimpleText(text, font.fontSize, font.compression, 0);
      textRow.items.push(rowItem);
    }

    return textRow;
  }

  private findNumberOfConnectedConnections(labelingContainer: ArticleContainer, caeItems: CaeItem[]): number {
    const caItemIds = caeItems.map((caeItem) => caeItem.id);
    return labelingContainer.connections.filter((connection) =>
      this.containerService.getParentArticleContainersByConnection(connection)
        .some((articleContainer: ArticleContainer) => caItemIds.includes(ArticleUsageUtils.getCompositeOrArticleUsageId(articleContainer.articleUsage))),
    ).length;
  }

  private calcFontSize(articleContainer: ArticleContainer,
                       textureElement: TextureAreaVO,
                       line: string,
                       rows: number,
                       columns: number,
                       textRotation: number): { fontSize: number, compression: number } {

    const textureAreaName: string = textureElement.name;
    const textureConnection: Connection = articleContainer.findConnectionByName(textureAreaName);
    const textCheckSpacingData: TextCheckSpacingData = this.labelingUtils.getTextCheckSpacingDataByTextureCube(textureConnection);
    const textSizeCheckClass: string = PropertyUtils.getStringPropertyValue(textureElement.behaviorProperties,
      MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS);

    const labelSizeInfos: TextSizeCheckVO[] = this.textSizeCheckManager.getTextSizeCheckWithFallback(
      textSizeCheckClass,
      textCheckSpacingData,
      ArticleUtils.isDynamicLabelingArticle(textureConnection.article),
    );

    let maxSizeTextInLines: number = TextSizeCheckUtils.getMaxSizeTextInLines([ line ]);
    // Aus Constaint-check-utils.ts line 66f kopiert - muss in Utils evtl allgemeinverwendbar umgebaut werden.
    maxSizeTextInLines = textRotation === 0 ?
      NumberUtils.roundValueByRoundOffLimit(maxSizeTextInLines / columns, InitManager.spacingDecidingValue, 1)
      : maxSizeTextInLines;
    let fontSize: number = null;
    const compression: number = TextSizeCheckUtils.getLeastCompression(
      labelSizeInfos, textSizeCheckClass, 1, maxSizeTextInLines, rows, 0, fontSize, 1,
    );

    if (NumberUtils.isNumber(compression) && NumberUtils.isNotNumber(fontSize)) {
      fontSize = TextSizeCheckUtils.getMaxFontSize(
        labelSizeInfos, textSizeCheckClass, 1, maxSizeTextInLines, rows, 0, compression, 1,
      );
    }
    if (NumberUtils.isNotNumber(fontSize)) {
      const defaultTextParameters: DefaultTextParameters =
        this.defaultTextParametersManager.getDefaultTextParameterByArticleContainer(articleContainer);
      if (defaultTextParameters) {
        fontSize = defaultTextParameters.fontSize;
      }
    }

    return {
      fontSize: fontSize,
      compression: compression,
    };

  }

  private createCompressionSimpleText(line: string, size: number, compression: number, textRotation: number): CompressionSimpleTextValueVO {
    const currentSize: number = NumberUtils.isNotNumber(size) ? 11 : size;
    const simpleValue: CompressionSimpleTextValueVO = new CompressionSimpleTextValueVO();
    simpleValue.hAlignment = 0;
    simpleValue.vAlignment = 0;
    simpleValue.rotation = textRotation;
    simpleValue.compression = NumberUtils.isNotNumber(compression) ? TextSizeCheckUtils.DEFAULT_COMPRESSION : compression;

    const htmlText: string = HtmlUtils.createLegacyFontParagraph(line, {
      align: 'CENTER',
      fontSize: currentSize,
      color: '#000000',
      bold: true,
    });
    simpleValue.text = htmlText;
    return simpleValue;
  }

  private getLabelOrBmk(level: CaeLevel, structure: string, bmkOptions: BmkOptions) {
    if (level?.name) {
      return level.name;
    } else {
      return this.createBmk(structure, bmkOptions);
    }
  }

  private createBmk(structure: string, bmkOptions: BmkOptions): string {
    if (StringUtils.isEmpty(structure)) {
      return '';
    }

    const result: BmkParseResult = BmkParser.parse(structure);

    let bmk = '';
    if (bmkOptions.bmkFunctionDesignation) bmk += result.functionDesignation;
    if (bmkOptions.bmkFunctionalAssignment) bmk += result.functionalAssignment;
    if (bmkOptions.bmkInstallationSite) bmk += result.installationSite;
    if (bmkOptions.bmkLocationDesignation) bmk += result.locationDesignation;
    if (bmkOptions.bmkDevice) bmk += result.device;

    if (StringUtils.isEmpty(bmk)) {
      return structure;
    } else {
      return bmk;
    }
  }

  private updateLabelOnTextureUsage(labels: TextValueVO[], articleUsage: ArticleUsageVO) {
    const textureAreaUsages: TextureAreaUsageVO[] = ArticleUsageUtils.textureAreaUsages(articleUsage);

    if (textureAreaUsages.length == 0) return;

    if (labels.length === textureAreaUsages.length) {
      for (let i = 0; i < textureAreaUsages.length; i++) {
        textureAreaUsages[i].textValue = labels[i];
      }
      return;
    }

    textureAreaUsages[0].textValue = labels[0];

    if (textureAreaUsages.length > 1) {
      textureAreaUsages[0].nextMergedUsageId = textureAreaUsages[1].identity;
    }

    for (let i = 1; i < textureAreaUsages.length; i++) {
      textureAreaUsages[i].previousMergedUsageId = textureAreaUsages[i - 1].identity;
      if (i < textureAreaUsages.length - 1) {
        textureAreaUsages[i].nextMergedUsageId = textureAreaUsages[i + 1].identity;
      }
    }
  }

  private async refreshTextureConnections(labelContainer: ArticleContainer[]) {
    const textureConnections = labelContainer.flatMap(container => container.connections).filter(connection => connection.isTextureArea());
    await this.loadConnectionTextureProcessor.refreshConnectionCubesAsync(textureConnections);
  }
}
